import { useId } from 'react';
import { X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage, Button } from '@monksflow/monks-ui';
import { getInitials } from '@/shared/utils/getInitials';
import { RoleSelector } from '../RoleSelector';
import type { MemberListItemProps } from './MemberListItem.types';
import { useTranslation } from 'react-i18next';

export function MemberListItem(props: MemberListItemProps) {
  const { t } = useTranslation('projects');
  const lockedReasonId = useId();
  const locked = props.lockedReason !== undefined;
  const canEditRole = props.canEditRole && !locked;

  return (
    <div className="flex items-center justify-between rounded-lg">
      <div className="flex items-center gap-3">
        <Avatar className="size-8">
          {props.member.avatarUrl && (
            <AvatarImage src={props.member.avatarUrl} alt={props.member.name} />
          )}
          <AvatarFallback>{getInitials(props.member.name)}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="text-sm text-text-primary">{props.member.name}</span>
          {props.member.email && (
            <span className="text-xs text-text-secondary">{props.member.email}</span>
          )}
        </div>
      </div>

      {props.isOwner ? (
        <span className="text-xs text-text-secondary">
          {t('projectMember.memberListItem.owner')}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          {props.roles && canEditRole && props.onRoleChange && (
            <RoleSelector
              roleId={props.roleId ?? ''}
              roles={props.roles}
              onRoleChange={props.onRoleChange}
              disabled={props.disabled}
              ariaLabel={props.roleSelectorLabel}
            />
          )}
          {props.roles && !canEditRole && (
            <span className="flex flex-col items-end text-xs text-text-secondary">
              {props.roleName ?? t('projectMember.roleSelector.noRole')}
              {locked && <span id={lockedReasonId}>{props.lockedReason}</span>}
            </span>
          )}
          {props.canRemove !== false && (
            <Button
              variant="ghost"
              size="sm"
              onClick={props.onRemove}
              // Kept in place rather than hidden, so the row lines up with the others and the
              // reason above says why it does nothing.
              disabled={props.disabled || locked}
              aria-describedby={locked ? lockedReasonId : undefined}
              aria-label={t('projectMember.memberListItem.removeMember', {
                name: props.member.name,
              })}
            >
              <X className="size-5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
